import clsx from "clsx";
import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  formatTimeRangeLabel,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  useDrawers,
  useFormats,
  useHaveCustomPermissions,
  useTimezone,
  type IHubBookingsPreviewRow,
  type IHubBookingsPreviewSummary,
} from "@ryde/shared";

import { FramerTooltip } from "../../tooltips";
import {
  BookingPassenger,
  BookingStation,
  formatHubTime,
  resolveHubBookingTimeLines,
} from "./parts";
import {
  HubBookingsAccordionButton,
  HubBookingsWarning,
  HubBookingsWrapper,
} from "./production-hub-bookings-preview.styled";

interface IProductionHubBookingsPreviewProps {
  hubBookingsPreview?: IHubBookingsPreviewRow[];
  hubBookingsPreviewSummary?: IHubBookingsPreviewSummary;
  hubAutoAssignedBookingIds?: number[];
  hubBookingsExcludedWithoutHubTypeIds?: number[];
  showJoinExcludedWarning?: boolean;
}

export const ProductionHubBookingsPreview: FC<
  IProductionHubBookingsPreviewProps
> = ({
  hubBookingsPreview,
  hubBookingsPreviewSummary,
  hubAutoAssignedBookingIds,
  hubBookingsExcludedWithoutHubTypeIds,
  showJoinExcludedWarning,
}) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  const { openBookingEditFormDrawer, openEditFormDrawer } = useDrawers();
  const {
    canViewBooking,
    canEditBooking,
    canEditStation,
    canOpenPassengerCard,
  } = useHaveCustomPermissions();
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(
    new Set(),
  );

  const toggleRowExpanded = useEvent((rowKey: string) => {
    setExpandedRowKeys(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  });

  const handleBookingClick = useEvent((bookingId: number) => {
    if (bookingId && canEditBooking) {
      openBookingEditFormDrawer({ id: bookingId });
    }
  });

  const handleStationClick = useEvent((stationId: number) => {
    if (stationId && canEditStation) {
      openEditFormDrawer({ entity: "station", id: stationId });
    }
  });

  const handlePassengerClick = useEvent((passengerId: number) => {
    if (passengerId && canOpenPassengerCard) {
      openEditFormDrawer({ entity: "passenger", id: passengerId });
    }
  });

  // Show when hub bookings exist even if grouping produced no preview rows (e.g. grouping edge case).
  // Also show when hub bookings are deferred (Hub type not selected) so the exclusion warning is visible.
  if (
    !hubBookingsPreview?.length &&
    !hubBookingsPreviewSummary?.hubBookingCount &&
    !hubBookingsExcludedWithoutHubTypeIds?.length
  ) {
    return null;
  }

  const totalBookings =
    hubBookingsPreview?.reduce(
      (sum, row) => sum + (row.bookings?.length ?? 0),
      0,
    ) ??
    // Fallback when rows are empty but summary reports hub bookings.
    hubBookingsPreviewSummary?.hubBookingCount ??
    0;
  const groupCount = hubBookingsPreview?.length ?? 0;

  return (
    <>
      {Boolean(hubAutoAssignedBookingIds?.length) && (
        <HubBookingsWarning>
          {t("rideProduction.hubAutoAssignedWarning", {
            count: hubAutoAssignedBookingIds?.length,
          })}
        </HubBookingsWarning>
      )}
      {Boolean(hubBookingsExcludedWithoutHubTypeIds?.length) && (
        <HubBookingsWarning>
          {t("rideProduction.hubTypeExcludedWarning", {
            count: hubBookingsExcludedWithoutHubTypeIds?.length,
          })}
        </HubBookingsWarning>
      )}
      {showJoinExcludedWarning &&
        Boolean(hubBookingsPreviewSummary?.hubBookingCount) && (
          <HubBookingsWarning>
            {/* Join production excludes hub bookings from the join matrix — warn only for Join action (see parent). */}
            {t("rideProduction.hubJoinExcludedWarning", {
              count: hubBookingsPreviewSummary?.hubBookingCount,
            })}
          </HubBookingsWarning>
        )}
      {Boolean(hubBookingsPreview?.length || hubBookingsPreviewSummary) && (
        <>
          <HubBookingsAccordionButton
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            $isOpen={isExpanded}
          >
            <span>{t("rideProduction.hubBookingsPreviewTitle")}</span>
            {groupCount > 0 && (
              <FramerTooltip
                content={t("rideProduction.hubBookingsPreviewGroupsTooltip", {
                  groups: groupCount,
                  bookings: totalBookings,
                })}
              >
                <span className="hub-schedule__count">
                  ({groupCount} / {totalBookings})
                </span>
              </FramerTooltip>
            )}
            {hubBookingsPreviewSummary && (
              <span className="hub-schedule__expected-rides">
                {hubBookingsPreviewSummary.expectedMainRideCount}{" "}
                {t("rideProduction.hubExpectedMainRides")} /{" "}
                {hubBookingsPreviewSummary.expectedPersonalRideCount}{" "}
                {t("rideProduction.hubExpectedPersonalRides")}
              </span>
            )}
            <span className="hub-schedule__chevron" aria-hidden />
          </HubBookingsAccordionButton>
          {isExpanded && hubBookingsPreview?.length ? (
            <HubBookingsWrapper>
              <div className="hub-schedule__row hub-schedule__row--header">
                <span>{t("rideProduction.hubBookingsHub")}</span>
                <span>{t("rideProduction.hubBookingsDirection")}</span>
                <span>{t("rideProduction.hubBookingsHubStation")}</span>
                <span>{t("rideProduction.hubBookingsSecondStation")}</span>
                <span>{t("rideProduction.hubBookingsShifts")}</span>
                <span className="hub-schedule__passengers">
                  {t("rideProduction.hubBookingsBookings")}
                </span>
              </div>
              {hubBookingsPreview.map(row => {
                // Group key mirrors backend mainHubSegmentGroups (hub + workplace station + direction).
                const rowKey = `${row.hubId}-${row.hubStationName ?? ""}-${row.direction}-${row.secondStationId}`;
                const isRowExpanded = expandedRowKeys.has(rowKey);
                const bookings = row.bookings ?? [];
                const hasBookings = bookings.length > 0;
                const shiftLabel =
                  formatTimeRangeLabel({
                    from: row.shiftFrom
                      ? formatHubTime({
                          dateISO: row.shiftFrom,
                          timeFormat,
                          timezone,
                        })
                      : "",
                    to: row.shiftTo
                      ? formatHubTime({
                          dateISO: row.shiftTo,
                          timeFormat,
                          timezone,
                        })
                      : "",
                  }) || NO_VALUE_DISPLAY;

                return (
                  <div key={rowKey}>
                    <div
                      className={clsx("hub-schedule__row", {
                        ["hub-schedule__row--expandable"]: hasBookings,
                      })}
                      onClick={
                        hasBookings
                          ? () => toggleRowExpanded(rowKey)
                          : undefined
                      }
                      onKeyDown={
                        hasBookings
                          ? onActivateKeyDown(() => toggleRowExpanded(rowKey))
                          : undefined
                      }
                      role={hasBookings ? "button" : undefined}
                      tabIndex={hasBookings ? 0 : undefined}
                    >
                      <span className="hub-schedule__name">#{row.hubId}</span>
                      <span>{row.direction}</span>
                      <span className="hub-schedule__name">
                        {row.hubStationName ?? NO_VALUE_DISPLAY}
                      </span>
                      <span className="hub-schedule__name">
                        {row.secondStationName ?? `#${row.secondStationId}`}
                      </span>
                      <span>{shiftLabel}</span>
                      <span className="hub-schedule__passengers">
                        {hasBookings && (
                          <button
                            type="button"
                            className="hub-schedule__expand-button"
                            onClick={e => {
                              e.stopPropagation();
                              toggleRowExpanded(rowKey);
                            }}
                            aria-label={
                              isRowExpanded
                                ? t("general.COLLAPSE")
                                : t("general.EXPAND")
                            }
                          >
                            <span
                              className={clsx("hub-schedule__expand-icon", {
                                ["hub-schedule__expand-icon--expanded"]:
                                  isRowExpanded,
                              })}
                            />
                          </button>
                        )}
                        {bookings.length}
                      </span>
                    </div>
                    {hasBookings &&
                      isRowExpanded &&
                      canViewBooking &&
                      bookings.map(booking => {
                        const timeLines = resolveHubBookingTimeLines({
                          booking,
                          direction: row.direction,
                          secondStationName: row.secondStationName,
                          timeFormat,
                          timezone,
                          t,
                        });

                        return (
                          <div
                            key={booking.id}
                            className="hub-schedule__row hub-schedule__row--booking"
                          >
                            <div className="hub-schedule__booking-main">
                              {canEditBooking ? (
                                <span
                                  className="hub-schedule__booking-id hub-schedule__booking-id--clickable"
                                  onClick={() => handleBookingClick(booking.id)}
                                  onKeyDown={onActivateKeyDown(() =>
                                    handleBookingClick(booking.id),
                                  )}
                                  role="button"
                                  tabIndex={0}
                                >
                                  #{booking.id}
                                </span>
                              ) : (
                                <span className="hub-schedule__booking-id">
                                  #{booking.id}
                                </span>
                              )}
                              <BookingPassenger
                                passengerId={booking.passengerId}
                                passengerFirstName={booking.passengerFirstName}
                                passengerLastName={booking.passengerLastName}
                                passengerWorkerCode={
                                  booking.passengerWorkerCode
                                }
                                canOpenPassengerCard={canOpenPassengerCard}
                                onPassengerClick={handlePassengerClick}
                              />
                            </div>
                            <span className="hub-schedule__booking-time">
                              {timeLines.length
                                ? timeLines.map(line => (
                                    <span
                                      key={line.label}
                                      className="hub-schedule__booking-time-line"
                                    >
                                      <span className="hub-schedule__booking-time-label">
                                        {line.label}:{" "}
                                      </span>
                                      {line.value}
                                    </span>
                                  ))
                                : NO_VALUE_DISPLAY}
                            </span>
                            <span className="hub-schedule__booking-stations">
                              <BookingStation
                                stationId={booking.originStationId}
                                stationName={booking.originStationName}
                                canEdit={canEditStation}
                                onStationClick={handleStationClick}
                              />
                              {" → "}
                              <BookingStation
                                stationId={booking.hubStationId}
                                stationName={booking.hubStationName}
                                canEdit={canEditStation}
                                onStationClick={handleStationClick}
                              />
                              {" → "}
                              <BookingStation
                                stationId={booking.destinationStationId}
                                stationName={booking.destinationStationName}
                                canEdit={canEditStation}
                                onStationClick={handleStationClick}
                              />
                            </span>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </HubBookingsWrapper>
          ) : null}
        </>
      )}
    </>
  );
};
